import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { QuickQuoteForm } from '../components/QuickQuoteForm';

export const TomografiaPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const carouselImages = [
    {
      src: "https://customer-assets.emergentagent.com/job_7899ab28-0d6a-4e3b-b0e7-b178b443ae26/artifacts/wfotg1pu_Aquilion%20Start.jpg",
      name: "Aquilion Start",
      description: "Sistema de entrada com 16 cortes - ideal para clínicas"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_7899ab28-0d6a-4e3b-b0e7-b178b443ae26/artifacts/wxzfsr2k_Aquilion%20Lightning.jpg",
      name: "Aquilion Lightning",
      description: "80-160 cortes - Performance e velocidade"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_7899ab28-0d6a-4e3b-b0e7-b178b443ae26/artifacts/a5wnx92i_Aquilion%20Prime.webp",
      name: "Aquilion Prime SP",
      description: "160 cortes - Alta performance com abertura de 78cm"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_7899ab28-0d6a-4e3b-b0e7-b178b443ae26/artifacts/ksfx6hht_Aquilion%20precision.webp",
      name: "Aquilion Precision",
      description: "160 cortes - Resolução ultra-alta de 0,15mm"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_7899ab28-0d6a-4e3b-b0e7-b178b443ae26/artifacts/ja5qu7co_Aquilion%20one.webp",
      name: "Aquilion ONE",
      description: "640 cortes - Topo de linha com cobertura de 16cm"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_7899ab28-0d6a-4e3b-b0e7-b178b443ae26/artifacts/ajqblx6n_Aquilion%20Exceed.jpg",
      name: "Aquilion Exceed LB",
      description: "64 cortes - Abertura de 90cm para radioterapia"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_7899ab28-0d6a-4e3b-b0e7-b178b443ae26/artifacts/wymmlts1_CElesteion.webp",
      name: "Celesteion PET/CT",
      description: "Sistema híbrido PET/CT para oncologia"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_7899ab28-0d6a-4e3b-b0e7-b178b443ae26/artifacts/shmw247a_Cartesion%20Prime.jpg",
      name: "Cartesion Prime",
      description: "Sistema avançado de imagem híbrida"
    }
  ];

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  }, [carouselImages.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  }, [carouselImages.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextSlide]);

  const families = [
    {
      name: "Linha Start",
      description: "Entrada acessível com tecnologia Canon",
      models: [
        {
          name: "Aquilion Start",
          slices: "16 cortes",
          description: "Sistema de entrada ideal para clínicas e hospitais que buscam qualidade Canon com investimento acessível.",
          highlights: ["Design compacto", "Fácil operação", "Baixo custo operacional"]
        }
      ]
    },
    {
      name: "Linha Lightning",
      description: "Performance e velocidade para alta demanda",
      models: [
        {
          name: "Aquilion Lightning",
          slices: "80 cortes",
          description: "Combinação ideal de velocidade e qualidade de imagem para rotinas de alto volume.",
          highlights: ["Alta velocidade de varredura", "Excelente qualidade de imagem", "Workflow otimizado"]
        },
        {
          name: "Aquilion Lightning SP",
          slices: "160 cortes",
          description: "Versão avançada com maior cobertura e recursos aprimorados.",
          highlights: ["160 cortes por rotação", "Cobertura ampliada", "Dose otimizada"]
        },
        {
          name: "Aquilion Lightning SP Smart Edition",
          slices: "160 cortes",
          description: "Edição inteligente com recursos de IA para workflow automatizado.",
          highlights: ["Inteligência Artificial", "Posicionamento automático", "Protocolos inteligentes"]
        }
      ]
    },
    {
      name: "Linha Prime",
      description: "Alta performance para diagnósticos avançados",
      models: [
        {
          name: "Aquilion Prime SP",
          slices: "160 cortes",
          description: "Sistema de alta performance com abertura de 78cm e tecnologias avançadas para cardíaco e rotina.",
          highlights: ["Abertura de 78cm", "Cardíaco avançado", "Redução de dose AIDR 3D"]
        }
      ]
    },
    {
      name: "Linha Precision",
      description: "Ultra-alta resolução para detalhes microscópicos",
      models: [
        {
          name: "Aquilion Precision",
          slices: "160 cortes",
          description: "Resolução espacial ultra-alta de 0,15mm para visualização de detalhes microscópicos.",
          highlights: ["Resolução de 0,15mm", "Detector de alta definição", "Ideal para ouvido interno e ossos"]
        }
      ]
    },
    {
      name: "Linha ONE",
      description: "Topo de linha com cobertura volumétrica de 16cm",
      models: [
        {
          name: "Aquilion ONE",
          slices: "640 cortes",
          description: "Tomógrafo de última geração com 640 cortes e cobertura de 16cm em uma única rotação.",
          highlights: ["640 cortes por rotação", "Cobertura de 16cm", "Cardiac CT em um batimento"]
        },
        {
          name: "Aquilion ONE / GENESIS Edition",
          slices: "640 cortes",
          description: "Edição com AiCE (Advanced intelligent Clear-IQ Engine) para reconstrução Deep Learning.",
          highlights: ["Deep Learning AiCE", "Menor ruído", "Dose ultra-baixa"]
        },
        {
          name: "Aquilion ONE / PRISM Edition",
          slices: "640 cortes",
          description: "CT espectral com Deep Learning Spectral para caracterização de tecidos.",
          highlights: ["CT Espectral", "Caracterização de tecidos", "Análise de materiais"]
        }
      ]
    },
    {
      name: "Linha Large Bore",
      description: "Abertura ampliada para radioterapia e pacientes bariátricos",
      models: [
        {
          name: "Aquilion LB",
          slices: "16 cortes",
          description: "Abertura de 90cm ideal para planejamento de radioterapia e pacientes com mobilidade limitada.",
          highlights: ["Abertura de 90cm", "Planejamento radioterápico", "Mesa de alta capacidade"]
        },
        {
          name: "Aquilion Exceed LB",
          slices: "64 cortes",
          description: "Versão avançada Large Bore com 64 cortes para maior velocidade e qualidade.",
          highlights: ["64 cortes", "Abertura de 90cm", "Simulação de radioterapia"]
        }
      ]
    },
    {
      name: "PET/CT",
      description: "Diagnóstico molecular combinado para oncologia, cardiologia e neurologia",
      models: [
        {
          name: "Celesteion",
          slices: "PET/CT",
          description: "Sistema híbrido PET/CT com abertura de 90cm para oncologia, cardiologia e neurologia.",
          highlights: ["PET/CT integrado", "Abertura de 90cm", "TOF (Time of Flight)"]
        },
        {
          name: "Cartesion Prime",
          slices: "PET/CT Digital",
          description: "PET/CT com detectores digitais avançados e IA (AiCE) para imagens de alta qualidade com doses baixas de radiofármacos.",
          highlights: [
            "Detectores digitais de alta resolução",
            "AiCE - Deep Learning para redução de ruído",
            "Refrigeração a ar (instalação simplificada)",
            "Design acessível para pacientes",
            "SURESubtraction™ para oncologia"
          ]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl font-bold text-white">
              Tomografia Computadorizada
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Linha completa de tomógrafos Canon Aquilion para todas as necessidades clínicas
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Carousel Section */}
      <section className="py-12 bg-gray-50">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Nossa Linha de Tomógrafos</h2>
            <p className="text-gray-600 mt-2">Explore nossos equipamentos</p>
          </div>
          
          {/* Main Carousel */}
          <div 
            className="relative"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            {/* Main Image Container */}
            <div className="relative overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="aspect-[16/9] md:aspect-[21/9] relative">
                {carouselImages.map((image, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                      index === currentSlide 
                        ? 'opacity-100 scale-100' 
                        : 'opacity-0 scale-105'
                    }`}
                  >
                    <img
                      src={image.src}
                      alt={image.name}
                      className="w-full h-full object-contain bg-gradient-to-b from-gray-50 to-gray-100"
                    />
                    {/* Overlay with info */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6 md:p-8">
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                        {image.name}
                      </h3>
                      <p className="text-white/90 text-sm md:text-base">
                        {image.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all hover:scale-110 z-10"
                data-testid="carousel-prev"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all hover:scale-110 z-10"
                data-testid="carousel-next"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Thumbnail Navigation */}
            <div className="mt-6 flex justify-center gap-2 md:gap-3 flex-wrap">
              {carouselImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`relative w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden transition-all duration-300 ${
                    index === currentSlide 
                      ? 'ring-4 ring-blue-600 scale-110 shadow-lg' 
                      : 'ring-2 ring-gray-200 hover:ring-blue-400 opacity-70 hover:opacity-100'
                  }`}
                  data-testid={`carousel-thumb-${index}`}
                >
                  <img
                    src={image.src}
                    alt={image.name}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>

            {/* Progress Dots */}
            <div className="flex justify-center gap-2 mt-4">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? 'w-8 bg-blue-600' 
                      : 'w-2 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-8 bg-white border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h3 className="text-center text-lg font-semibold text-gray-700 mb-4">Navegue por Linha</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {families.map((family) => (
              <a 
                key={family.name}
                href={`#${family.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="px-4 py-2 bg-gray-100 rounded-full text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors border hover:border-blue-300"
              >
                {family.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Families */}
      <section className="py-12 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {families.map((family, familyIndex) => (
              <div key={family.name} id={family.name.toLowerCase().replace(/\s+/g, '-')}>
                <div className="text-center mb-10">
                  <h2 className="text-3xl font-bold text-gray-900">{family.name}</h2>
                  <p className="text-gray-600 mt-2">{family.description}</p>
                </div>
                
                <div className={`grid gap-8 ${family.models.length === 1 ? 'grid-cols-1 max-w-2xl mx-auto' : family.models.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                  {family.models.map((model, modelIndex) => (
                    <Card key={model.name} className="hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                      <div className="h-3 bg-gradient-to-r from-blue-500 to-cyan-500"></div>
                      <CardContent className="p-6 space-y-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{model.name}</h3>
                            <span className="inline-block mt-1 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                              {model.slices}
                            </span>
                          </div>
                        </div>
                        
                        <p className="text-gray-600 text-sm">
                          {model.description}
                        </p>
                        
                        <ul className="space-y-2">
                          {model.highlights.map((highlight, idx) => (
                            <li key={idx} className="flex items-center space-x-2 text-sm">
                              <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                              <span className="text-gray-700">{highlight}</span>
                            </li>
                          ))}
                        </ul>
                        
                        <Link to="/contact" className="block">
                          <Button variant="outline" className="w-full border-blue-600 text-blue-600 hover:bg-blue-50">
                            Solicitar Informações
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Quote Section */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-xl px-4 sm:px-6 lg:px-8">
          <QuickQuoteForm equipmentName="Tomografia" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Não sabe qual modelo escolher?
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Nossa equipe de especialistas pode ajudá-lo a encontrar o tomógrafo ideal para sua instituição
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Fale com um Especialista
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
